<template>
    <v-container fluid>
        <v-alert dense text type="info" border="left" class="mb-4">
            O <strong>valor padrão</strong> é o valor que cada usuário deste perfil recebe ao abrir a tela.
            Com a <strong>trava</strong> ativa, o usuário não pode alterar o recurso na sessão.
        </v-alert>

        <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Buscar recurso"
                dense
                outlined
                clearable
                hide-details
                class="mb-3"
            />

            <v-chip-group v-model="selectedContext" mandatory column class="mb-3">
                <v-chip
                    v-for="ctx in contexts"
                    :key="ctx.key"
                    :value="ctx.key"
                    small
                    filter
                    active-class="primary white--text"
                >
                    {{ ctx.label }}
                </v-chip>
            </v-chip-group>

            <div v-for="(items, group) in grouped" :key="group" class="mb-5">
                <div class="d-flex align-center mb-2">
                    <v-icon small color="grey darken-1" class="mr-2">mdi-tune-variant</v-icon>
                    <span class="subtitle-2 grey--text text--darken-2">{{ group }}</span>
                </div>

                <v-card outlined>
                    <div class="feature-row feature-head">
                        <span class="head-cell">
                            Valor padrão
                            <tooltips-helper
                                hide-title
                                color="grey darken-1"
                                max-width="280px"
                                text="Estado em que o recurso já vem ligado ou desligado quando um usuário deste perfil abre a tela. Ligado libera o recurso; desligado mantém a regra padrão do sistema."
                            />
                        </span>
                        <span>Recurso</span>
                        <span class="head-cell justify-center">
                            Trava
                            <tooltips-helper
                                hide-title
                                color="grey darken-1"
                                max-width="280px"
                                text="Cadeado fechado impede que o usuário ligue ou desligue este recurso durante o uso da tela — ele fica sempre no valor padrão. Cadeado aberto deixa o usuário alterar quando precisar."
                            />
                        </span>
                    </div>
                    <v-divider />

                    <template v-for="(feature, idx) in items">
                        <v-divider v-if="idx > 0" :key="'d-' + feature.id" />
                        <div :key="feature.id" class="feature-row">
                            <div>
                                <tooltips-body :tooltip-text="valueHint(feature.id)">
                                    <template v-slot:body="{ on, attrs }">
                                        <div v-bind="attrs" v-on="on" class="d-inline-block">
                                            <v-switch
                                                v-model="state[feature.id].value"
                                                color="green darken-2"
                                                hide-details
                                                dense
                                                class="pa-0 ma-0"
                                                @change="emitPayload"
                                            />
                                        </div>
                                    </template>
                                </tooltips-body>
                                <div class="caption grey--text text--darken-1">
                                    {{ state[feature.id].value ? 'Ligado' : 'Desligado' }}
                                </div>
                            </div>
                            <div>
                                <div class="body-2">{{ feature.label }}</div>
                                <div class="caption grey--text text--darken-1">
                                    {{ feature.description }}
                                </div>
                                <div class="caption mt-1 grey--text text--darken-1">
                                    {{ effectHint(feature.id) }}
                                </div>
                            </div>
                            <div class="text-center">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                            :color="state[feature.id].locked ? 'blue darken-2' : 'grey'"
                                            @click="toggleLock(feature.id)"
                                        >
                                            <v-icon>{{ state[feature.id].locked ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{ state[feature.id].locked ? 'Travado: usuário não pode alterar' : 'Liberado: usuário pode alterar' }}</span>
                                </v-tooltip>
                                <div class="caption grey--text text--darken-1">
                                    {{ state[feature.id].locked ? 'Travado' : 'Liberado' }}
                                </div>
                            </div>
                        </div>
                    </template>
                </v-card>
            </div>

            <div v-if="!hasResults" class="text-center grey--text py-6">
                <v-icon color="grey lighten-1">mdi-magnify-close</v-icon>
                <div class="caption mt-1">Nenhum recurso encontrado.</div>
            </div>
    </v-container>
</template>

<script setup>

import { defineEmits, defineProps, ref, computed, onBeforeMount, watch } from 'vue'
import { useStore } from '@/composables/useStore'

const store = useStore()

const emit = defineEmits(['change'])

const props = defineProps({
    role: { type: Object, default: () => ({}) }
})

const catalog = ref([])
const state = ref({})
const search = ref('')
const selectedContext = ref(null)

const toBool = (val) => val === true || val === 1 || val === '1'

// Rótulo amigável de cada contexto/módulo. Novos contextos caem no próprio nome até serem mapeados.
const contextLabels = {
    schedule: 'Alocação de horário',
}

// Tags de contexto (distintas) montadas a partir do catálogo.
const contexts = computed(() => {
    const list = []
    catalog.value.forEach((feature) => {
        if (!list.some((c) => c.key === feature.context)) {
            list.push({ key: feature.context, label: contextLabels[feature.context] || feature.context })
        }
    })
    return list
})

// Recursos do contexto selecionado, filtrados pela busca e agrupados por "group".
const grouped = computed(() => {
    const term = (search.value || '').toLowerCase()

    return catalog.value
        .filter((feature) => feature.context === selectedContext.value)
        .filter((feature) => {
            if (!term) return true
            return [feature.label, feature.description, feature.key]
                .some((v) => (v || '').toLowerCase().includes(term))
        })
        .reduce((acc, feature) => {
            const group = feature.group || 'Outros'
            ;(acc[group] = acc[group] || []).push(feature)
            return acc
        }, {})
})

const hasResults = computed(() => Object.keys(grouped.value).length > 0)

const loadCatalog = async () => {
    const res = await store.dispatch('role/ActionFeatureCatalog', {})
    catalog.value = res.data || []
    if (!selectedContext.value && contexts.value.length) {
        selectedContext.value = contexts.value[0].key
    }
    buildState()
}

const buildState = () => {
    const saved = {}
    ;(props.role.features || []).forEach((f) => {
        saved[f.id] = f.pivot || {}
    })

    const next = {}
    catalog.value.forEach((feature) => {
        const current = saved[feature.id]
        // sem linha explícita: usa o default do catálogo (padrão conservador: desligado)
        next[feature.id] = {
            value: current ? toBool(current.value) : toBool(feature.default_value),
            locked: current ? toBool(current.locked) : false
        }
    })
    state.value = next

    emitPayload()
}

// Texto do tooltip do switch — descreve o que o valor default faz com o usuário do perfil.
const valueHint = (featureId) => {
    return state.value[featureId] && state.value[featureId].value
        ? 'Usuários deste perfil abrem a tela com este recurso já liberado.'
        : 'Usuários deste perfil abrem a tela sem este recurso — vale a regra padrão do sistema.'
}

// Resumo em uma linha do efeito combinado de default + trava.
const effectHint = (featureId) => {
    const current = state.value[featureId] || {}
    if (current.locked) {
        return current.value
            ? 'Sempre ligado — o usuário não pode desligar'
            : 'Sempre desligado — o usuário não pode ligar'
    }
    return current.value
        ? 'Abre ligado — o usuário pode desligar na tela'
        : 'Abre desligado — o usuário pode ligar na tela'
}

const toggleLock = (featureId) => {
    state.value[featureId].locked = !state.value[featureId].locked
    emitPayload()
}

const emitPayload = () => {
    const payload = catalog.value.map((feature) => ({
        feature_id: feature.id,
        value: state.value[feature.id].value,
        locked: state.value[feature.id].locked
    }))
    emit('change', payload)
}

watch(() => props.role.id, () => {
    if (catalog.value.length) buildState()
})

onBeforeMount(loadCatalog)

</script>

<style scoped>
.feature-row {
    display: grid;
    grid-template-columns: 88px 1fr 88px;
    gap: 12px;
    align-items: center;
    padding: 8px 16px;
}
.feature-head {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    padding-top: 10px;
    padding-bottom: 10px;
}
.theme--dark .feature-head {
    color: rgba(255, 255, 255, 0.5);
}
.head-cell {
    display: flex;
    align-items: center;
    gap: 4px;
}
</style>
